import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import { useApis } from "../../hooks/useApis";
import { useProducts } from "../../hooks/useProducts";
import type { KafkaEventTopic, EntityStatus } from "../../types";
import {
  DataSensitivity,
  KafkaSchemaFormat,
  SchemaCompatibilityLevel,
} from "../../types";

interface EditKafkaEventTopicDialogProps {
  item: KafkaEventTopic | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: KafkaEventTopic) => void;
}

const SCHEMA_FORMATS = Object.values(KafkaSchemaFormat);
const COMPATIBILITY_LEVELS = Object.values(SchemaCompatibilityLevel);
const SENSITIVITY_LEVELS = Object.values(DataSensitivity);

const EditKafkaEventTopicDialog: React.FC<EditKafkaEventTopicDialogProps> = ({
  item,
  open,
  onClose,
  onSaved,
}) => {
  const { apis } = useApis();
  const { products } = useProducts();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<KafkaEventTopic | null>(item);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(item);
  }, [item]);

  const handleSchemaFile = async (file: File) => {
    const text = await file.text();
    if (draft?.schemaFormat === KafkaSchemaFormat.JSON) {
      try {
        JSON.parse(text);
      } catch {
        alert("Schema file must be valid JSON for JSON format");
        return;
      }
    }
    setDraft((prev) => (prev ? { ...prev, schemaContent: text } : prev));
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await apiService.updateKafkaEventTopic(draft.uuid, {
        name: draft.name,
        description: draft.description ?? undefined,
        status: draft.status,
        classifications: draft.classifications ?? undefined,
        entityClass: draft.entityClass ?? undefined,
        environment: draft.environment,
        purpose: draft.purpose ?? undefined,
        schemaFormat: draft.schemaFormat,
        schemaContent: draft.schemaContent,
        schemaVersion: draft.schemaVersion ?? undefined,
        schemaSubject: draft.schemaSubject ?? undefined,
        schemaRegistryUrl: draft.schemaRegistryUrl ?? undefined,
        compatibilityLevel: draft.compatibilityLevel ?? undefined,
        keySchemaFormat: draft.keySchemaFormat ?? undefined,
        keySchemaContent: draft.keySchemaContent ?? undefined,
        partitionCount: draft.partitionCount ?? undefined,
        replicationFactor: draft.replicationFactor ?? undefined,
        retentionMs: draft.retentionMs ?? undefined,
        samplePayload: draft.samplePayload ?? undefined,
        dataSensitivity: draft.dataSensitivity ?? undefined,
        notes: draft.notes ?? undefined,
        productUuids: draft.products?.map((p) => p.uuid) ?? [],
        apiUuids: draft.apis?.map((a) => a.uuid) ?? [],
      });
      onSaved(saved);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Kafka event topic</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          label="Topic name"
          margin="normal"
          value={draft?.name ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Environment"
          margin="normal"
          value={draft?.environment ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, environment: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Purpose"
          margin="normal"
          multiline
          minRows={2}
          value={draft?.purpose ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, purpose: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          select
          label="Status"
          margin="normal"
          value={draft?.status ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, status: e.target.value as EntityStatus } : prev,
            )
          }
        >
          {(["DRAFT", "ACTIVE", "INACTIVE", "ARCHIVED"] as EntityStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          label="Schema format"
          margin="normal"
          value={draft?.schemaFormat ?? KafkaSchemaFormat.JSON}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? { ...prev, schemaFormat: e.target.value as KafkaSchemaFormat }
                : prev,
            )
          }
        >
          {SCHEMA_FORMATS.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() => fileInputRef.current?.click()}
        >
          Replace schema file
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,.avsc,.txt,text/plain,application/json"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleSchemaFile(file);
          }}
        />
        <TextField
          fullWidth
          label="Schema content"
          margin="normal"
          multiline
          minRows={6}
          value={draft?.schemaContent ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, schemaContent: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          label="Schema version"
          margin="normal"
          value={draft?.schemaVersion ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, schemaVersion: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          select
          label="Compatibility level"
          margin="normal"
          value={draft?.compatibilityLevel ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? {
                    ...prev,
                    compatibilityLevel: (e.target.value ||
                      null) as SchemaCompatibilityLevel | null,
                  }
                : prev,
            )
          }
        >
          <MenuItem value="">None</MenuItem>
          {COMPATIBILITY_LEVELS.map((level) => (
            <MenuItem key={level} value={level}>
              {level}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          label="Data sensitivity"
          margin="normal"
          value={draft?.dataSensitivity ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? {
                    ...prev,
                    dataSensitivity: (e.target.value || null) as DataSensitivity | null,
                  }
                : prev,
            )
          }
        >
          <MenuItem value="">None</MenuItem>
          {SENSITIVITY_LEVELS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          multiple
          options={products}
          getOptionLabel={(p) => p.name}
          value={draft?.products ?? []}
          onChange={(_, value) =>
            setDraft((prev) => (prev ? { ...prev, products: value } : prev))
          }
          renderInput={(params) => (
            <TextField {...params} label="Linked products" margin="normal" />
          )}
        />
        <Autocomplete
          multiple
          options={apis}
          getOptionLabel={(a) => a.name}
          value={draft?.apis ?? []}
          onChange={(_, value) =>
            setDraft((prev) => (prev ? { ...prev, apis: value } : prev))
          }
          renderInput={(params) => (
            <TextField {...params} label="Linked APIs" margin="normal" />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditKafkaEventTopicDialog;
